/**
 * MSAL bootstrap — Admin Panel.
 *
 * Plain-browser, mypcb-tenant Entra SSO. Admin users are bank staff, so we
 * point at tenant `0f60eb50-7ea7-4db3-b65b-0bc945f33568` (Phelps County
 * Bank). The dedicated app registration is `PCB Admin Panel`
 * (`cc1002aa-294e-4abf-9e78-126b43cdafbb`); SPA + PKCE + delegated
 * Microsoft Graph User.Read.
 *
 * Redirect URIs configured on the app reg:
 *   - https://admin.mypcb.com/                                  (prod)
 *   - https://wonderful-tree-055bf2810.7.azurestaticapps.net/   (default SWA hostname)
 *   - http://localhost:53040/                                   (dev)
 */

import {
  PublicClientApplication,
  LogLevel,
  type Configuration,
} from '@azure/msal-browser';

const DEFAULT_CLIENT_ID = 'cc1002aa-294e-4abf-9e78-126b43cdafbb';
const DEFAULT_TENANT_ID = '0f60eb50-7ea7-4db3-b65b-0bc945f33568';

const clientId =
  (import.meta.env.VITE_AAD_CLIENT_ID as string | undefined) ?? DEFAULT_CLIENT_ID;
const tenantId =
  (import.meta.env.VITE_AAD_TENANT_ID as string | undefined) ?? DEFAULT_TENANT_ID;

/** Graph User.Read for login. The PCB tenant blocks user-consent for custom
 *  APIs, so we cannot request api://<backend> without admin consent; instead the
 *  crypto-validatable token is sourced from the ID token in getMsalToken (no
 *  consent needed). Bill 2026-06-18. */
export const API_SCOPES = ['User.Read', 'openid', 'profile', 'email'];

export const MSAL_CONFIG: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri:
      typeof window !== 'undefined' ? `${window.location.origin}/` : '/',
    postLogoutRedirectUri:
      typeof window !== 'undefined' ? `${window.location.origin}/` : '/',
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      piiLoggingEnabled: false,
      loggerCallback: (level, message) => {
        if (level <= LogLevel.Warning) {
          // eslint-disable-next-line no-console
          console.warn('[admin-msal]', message);
        }
      },
    },
  },
};

export const msalInstance = new PublicClientApplication(MSAL_CONFIG);

let msalReadyPromise: Promise<void> | null = null;
export function ensureMsalReady(): Promise<void> {
  if (!msalReadyPromise) {
    msalReadyPromise = (async () => {
      await msalInstance.initialize();
      await msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: false });
    })();
  }
  return msalReadyPromise;
}

/**
 * One token request at a time (Bill 2026-10-06: "Authentication required" on every page). Pages fire
 * several calls at once; parallel acquireTokenSilent calls raced, one started a redirect and the rest
 * threw interaction_in_progress — those requests went out with no token. Callers now share one
 * in-flight request.
 */
let inFlight: Promise<string> | null = null;

export function getMsalToken(scopes: string[] = API_SCOPES): Promise<string> {
  if (!inFlight) {
    inFlight = acquireIdToken(scopes).finally(() => { inFlight = null; });
  }
  return inFlight;
}

/** Seconds left on an ID token, from its exp claim (0 when unreadable). */
function secondsLeft(claims: Record<string, unknown> | undefined): number {
  const exp = typeof claims?.exp === 'number' ? claims.exp : 0;
  return exp - Math.floor(Date.now() / 1000);
}

async function acquireIdToken(scopes: string[]): Promise<string> {
  await ensureMsalReady();

  const accounts = msalInstance.getAllAccounts();
  const firstAccount = accounts[0];

  if (firstAccount) {
    try {
      let result = await msalInstance.acquireTokenSilent({
        scopes,
        account: firstAccount,
      });
      // Send the ID token (aud = this SPA's client id, tenant-signed, NO Graph
      // nonce) so the backend BearerTokenValidator.ValidateIdentityAsync can
      // CRYPTOGRAPHICALLY validate it (sig + issuer + lifetime). The Graph
      // access token can't be validated by anyone but Graph; the ID token can,
      // and needs no api-scope admin consent. Bill 2026-06-18.
      //
      // MSAL's silent call only checks the GRAPH token's expiry, never the ID token's, so after
      // about an hour it kept handing back an expired ID token and the API answered 401
      // (2026-10-06). Refresh when it has under 5 minutes left.
      if (secondsLeft(result.idTokenClaims as Record<string, unknown> | undefined) < 300) {
        result = await msalInstance.acquireTokenSilent({ scopes, account: firstAccount, forceRefresh: true });
      }
      return result.idToken;
    } catch {
      // Silent failed — interactive redirect below.
    }
  }

  // Interactive sign-in via full-page redirect (NOT loginPopup, which the
  // browser popup-blocker kills when fired on load without a user gesture).
  // Reuses the Edge/Windows work session; handleRedirectPromise() in
  // ensureMsalReady() completes the round-trip on return, then the next call
  // resolves silently from the cached account.
  await msalInstance.loginRedirect({ scopes });
  return new Promise<string>(() => { /* unreachable — page redirected */ });
}
